import {getSubtreeAtIndex, getValuesAtIndexTreeRoot, IndexTreeIndex, MaybeIndexTree} from 'util/IndexTree';
import pick from 'util/pick';

import {getSelectedSearchResultDataAttrs, MEDIOCRE_SEARCH_RESULT_ID_DATA_ATTRIBUTE} from './DOM';
import {MediocreSearchResult} from './SearchResults';

/**
 * For high performance rendering, parents are expected to pass down the following props to its children.
 *
 * If the parent component fans out to multiple children, it can use `getSearchResultPropsForIndex`,
 * in conjunction with defining an appropriate value for `MediocreSearchResult.index` in the plugin's
 * getSearchResults implementation.
 *
 * If the parent component needs to pass the props down through its hierarchy, it can use
 * `forwardSearchResultProps`.
 */
export interface MediocreSearchResultProps {
  searchResultsTree: MaybeIndexTree<MediocreSearchResult>;
  selectedSearchResultTree: MaybeIndexTree<MediocreSearchResult>;
}

// Allow a caller to pass in any object that extends MediocreSearchResultProps
interface ExtendedSearchResultProps extends MediocreSearchResultProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [otherKey: string | number | symbol]: any;
}

export const EMPTY_SEARCH_RESULT_PROPS = {
  selectedSearchResultTree: null,
  searchResultsTree: null,
};

export function getSearchResultPropsForIndex(
  props: ExtendedSearchResultProps = EMPTY_SEARCH_RESULT_PROPS,
  index: IndexTreeIndex
): MediocreSearchResultProps {
  return {
    searchResultsTree: getSubtreeAtIndex(props.searchResultsTree, index),
    selectedSearchResultTree: getSubtreeAtIndex(props.selectedSearchResultTree, index),
  };
}

export function forwardSearchResultProps(
  props: ExtendedSearchResultProps = EMPTY_SEARCH_RESULT_PROPS
): MediocreSearchResultProps {
  return pick(props, 'searchResultsTree', 'selectedSearchResultTree');
}

export function getSearchResultsAtRoot(props: ExtendedSearchResultProps): {
  results: Array<MediocreSearchResult>;
  selected: MediocreSearchResult | null;
} {
  const [selected] = getValuesAtIndexTreeRoot(props.selectedSearchResultTree);
  return {
    results: getValuesAtIndexTreeRoot(props.searchResultsTree),
    selected: selected ?? null,
  };
}

export function getSelectedSearchResultProps(props: MediocreSearchResultProps): {
  [MEDIOCRE_SEARCH_RESULT_ID_DATA_ATTRIBUTE]?: string;
} {
  return getSelectedSearchResultDataAttrs(getSearchResultsAtRoot(props).selected?.uuid);
}
